import { Breadcrumb } from "@hellocarbo/lasagne";
import { cn } from "@hellocarbo/lasagne/utils";
import { use } from "react";
import {
  data,
  LoaderFunctionArgs,
  Outlet,
  useLoaderData,
  useOutletContext,
} from "react-router";
import invariant from "tiny-invariant";

import { PortalContainerContext, UserContext } from "@/contexts";
import useBreadcrumbs from "@/hooks/useBreadcrumbs";
import {
  Industry as TIndustry,
  Review as TReview,
  TDynamicCategory,
} from "@/types";

export async function loader({ params, request }: LoaderFunctionArgs) {
  const reviewId = params.reviewId || params.slug;

  invariant(reviewId, "No review found!");

  const isPublic = request.url.includes("public");
  const endpoint = isPublic
    ? `/api/client/reviews/${reviewId}/public/`
    : `/api/client/reviews/${reviewId}`;

  const reviewResponse = await fetch(endpoint);

  if (reviewResponse.status !== 200) {
    throw data(`Failed to load the ${isPublic ? "public " : ""}review data.`, {
      status: reviewResponse.status,
    });
  }

  const { data: reviewData } = (await reviewResponse.json()) as {
    data: TReview;
  };

  const industryId = reviewData.industry_id;
  invariant(industryId, "No industry id found!");

  const industryResponse = await fetch(
    `/api/client/industry/${industryId}/parent`
  );
  if (industryResponse.status !== 200) {
    throw data("Failed to load the industry data.", {
      status: industryResponse.status,
    });
  }

  const { data: industryData } = (await industryResponse.json()) as {
    data: TIndustry;
  };

  // Review categories
  const categoriesResponse = await fetch(`/api/client/review_categories`);
  if (categoriesResponse.status !== 200) {
    throw data("Failed to load the categories", {
      status: categoriesResponse.status,
    });
  }
  const { data: categories } = (await categoriesResponse.json()) as {
    data: TDynamicCategory[];
  };

  return { reviewData, industryData, isPublic, categories };
}

type ContextType = {
  reviewData: TReview;
  industryData: TIndustry;
};

export function useReviewContext() {
  return useOutletContext<ContextType>();
}

export const overlayedClassname =
  "py-sm absolute left-[210px] z-[-1] w-[calc(100vw_-_210px)] border-b border-white/80 bg-white/80 px-[3rem] shadow-sm backdrop-blur-md";

const Review = () => {
  const { reviewData, industryData, categories } =
    useLoaderData<typeof loader>();
  const { crumbs, isOverlayed } = useBreadcrumbs(categories);

  const { user } = use(UserContext);
  const { container } = use(PortalContainerContext);

  return (
    <div
      className={cn(
        isOverlayed
          ? overlayedClassname
          : "flex min-h-full w-[calc(100vw_-_210px)] flex-col",
        !user && "left-0 w-screen"
      )}
    >
      <div
        className={cn("flex flex-1 flex-col", !isOverlayed && "mx-xl my-lg")}
      >
        <div className={cn(!isOverlayed && "mb-sm")}>
          <Breadcrumb portalContainer={container!} crumbs={crumbs} />
        </div>
        <div className="flex flex-1 flex-col">
          <Outlet context={{ reviewData, industryData }} />
        </div>
      </div>
    </div>
  );
};

export default Review;
